// Firebase bootstrap with graceful degradation. Everything here is optional:
// if config is missing, a provider isn't enabled yet, or the browser blocks
// storage, the app keeps working on-device. This lets us wire identity and
// analytics now and have them light up the moment the remaining console
// toggles (Anonymous auth provider, Google Analytics) are flipped.
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, // absent until GA is enabled
};

export const firebaseEnabled = Boolean(config.apiKey && config.projectId);

let app = null;
let authInstance = null;
let dbInstance = null;

if (firebaseEnabled) {
  try {
    app = initializeApp(config);
    authInstance = getAuth(app);
    dbInstance = getFirestore(app);
  } catch (e) {
    console.warn('[firebase] init failed; running on-device only', e?.message);
  }
}

/**
 * Silently sign the visitor in anonymously so preferences and contributions
 * can sync to the cloud with a stable id — no login UI, no friction. Returns
 * the uid, or null if auth isn't available/enabled yet (app still works).
 */
export function ensureAnonymousAuth() {
  if (!authInstance) return Promise.resolve(null);
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(authInstance, (user) => {
      if (user) {
        unsub();
        resolve(user.uid);
      }
    });
    signInAnonymously(authInstance).catch((e) => {
      // Provider not enabled yet (auth/operation-not-allowed / configuration-not-found)
      unsub();
      resolve(null);
      if (import.meta.env.DEV) console.info('[firebase] anonymous auth unavailable:', e?.code);
    });
  });
}

export function getDb() {
  return dbInstance;
}

// Analytics is lazy + guarded: getAnalytics() requires a measurementId, which
// only exists once Google Analytics is linked. Until then every event is a
// safe no-op, so callers can instrument freely today.
let analyticsPromise;
async function getAnalyticsSafe() {
  if (!app || !config.measurementId) return null;
  if (analyticsPromise === undefined) {
    analyticsPromise = (async () => {
      try {
        const { getAnalytics, isSupported } = await import('firebase/analytics');
        return (await isSupported()) ? getAnalytics(app) : null;
      } catch {
        return null;
      }
    })();
  }
  return analyticsPromise;
}

/** Log a funnel/product event. No-ops until Google Analytics is enabled. */
export async function track(eventName, params = {}) {
  const analytics = await getAnalyticsSafe();
  if (!analytics) return;
  try {
    const { logEvent } = await import('firebase/analytics');
    logEvent(analytics, eventName, params);
  } catch {
    /* ignore */
  }
}
